import {
  SETTINGS_MANAGE,
  SETTINGS_VIEW,
  SHARED_SETTINGS_VIEW,
  USERS_MANAGE,
} from 'constants/permissions'
import { ReactLazyPreload } from 'utilities/PreloadLinks'
import type { Route } from './types'
import { useShopSite } from 'api/ShopSite'

const SettingsDirectory = ReactLazyPreload(() => import('../components/SettingsDirectory/index'))

const OpeningHours = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/OpeningHours').then((file) => {
    return { default: file.OpeningHours }
  })
)

const Zones = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/Zones').then((file) => {
    return { default: file.Zones }
  })
)

const PaymentTypes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/PaymentTypes').then((file) => {
    return { default: file.PaymentTypes }
  })
)

const RevenueCentres = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Organisation/RevenueCentres').then((file) => {
    return { default: file.RevenueCentres }
  })
)

const OfferingAvailabilityCalendar = ReactLazyPreload(
  () => import('../screens/OfferingAvailabilityCalendar/OfferingAvailabilityCalendar')
)

const Practitioners = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Resources/Practitioners').then((file) => {
    return { default: file.Practitioners }
  })
)

const Categories = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/Categories').then((file) => {
    return { default: file.Categories }
  })
)

const VoucherDelivery = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/VoucherDelivery').then((file) => {
    return { default: file.VoucherDelivery }
  })
)

const Rooms = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Resources/Rooms').then((file) => {
    return { default: file.Rooms }
  })
)

const BookableAreas = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Resources/BookableAreas').then((file) => {
    return { default: file.BookableAreas }
  })
)

const Equipment = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Resources/Equipment').then((file) => {
    return { default: file.Equipment }
  })
)

const BlockedTimePresets = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Resources/BlockedTimePresets').then((file) => {
    return { default: file.BlockedTimePresets }
  })
)

const AppointmentTypes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/AppointmentTypes').then((file) => ({
    default: file.AppointmentTypes,
  }))
)

const SessionTypes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/SessionTypes').then((file) => ({
    default: file.SessionTypes,
  }))
)

const AreaBookingTypes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/AreaBookingTypes').then((file) => {
    return { default: file.AreaBookingTypes }
  })
)

const RetailProducts = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/RetailProducts').then((file) => ({
    default: file.RetailProducts,
  }))
)

const TableReservationTypes = ReactLazyPreload(
  () => import('../components/SettingsDirectory/Offerings/TableReservationTypes')
)

const Packages = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/Packages').then((file) => ({
    default: file.Packages,
  }))
)

const Courses = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/Courses').then((file) => ({
    default: file.Courses,
  }))
)

const VoucherTypes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Offerings/VoucherTypes').then((file) => ({
    default: file.VoucherTypes,
  }))
)

const Membership = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Clients/Membership').then((file) => {
    return { default: file.Membership }
  })
)

const CustomerLabels = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Clients/ClientLabels').then((file) => {
    return { default: file.ClientLabels }
  })
)

const IntakeFormBuilder = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Clients/IntakeFormBuilder').then((file) => {
    return { default: file.IntakeFormBuilder }
  })
)

const ShopSettings = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Marketing/Shop').then((file) => {
    return { default: file.ShopSettings }
  })
)

const Legal = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Marketing/Legal').then((file) => {
    return { default: file.Legal }
  })
)

const MarketingPreferences = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Marketing/MarketingPreferences').then((file) => {
    return { default: file.MarketingPreferences }
  })
)

const EmailBranding = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Marketing/EmailBranding').then((file) => {
    return { default: file.EmailBranding }
  })
)

const Emails = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Emails').then((file) => {
    return { default: file.EmailsSettings }
  })
)

const SmsNotifications = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Marketing/SmsNotifications').then((file) => {
    return { default: file.SmsNotifications }
  })
)

const VisitTypes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Marketing/VisitTypes').then((file) => {
    return { default: file.VisitTypes }
  })
)

const DiscountTypesAndPromoCodes = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Orders/DiscountTypesAndPromoCodes').then((file) => ({
    default: file.DiscountTypesAndPromoCodes,
  }))
)

const SalesChannels = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Orders/SalesChannels').then((file) => ({
    default: file.SalesChannels,
  }))
)

const OrderLabels = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Orders/OrderLabels').then((file) => ({
    default: file.OrderLabels,
  }))
)

const AutoCheckout = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Orders/AutoCheckout').then((file) => ({
    default: file.AutoCheckout,
  }))
)

const AppointmentEnquiries = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/AppointmentEnquiries').then((file) => ({
    default: file.AppointmentEnquiries,
  }))
)

const ServiceCharge = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Orders/ServiceCharge').then((file) => ({
    default: file.ServiceCharge,
  }))
)

const InventoryCategories = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/InventoryCategories').then((file) => ({
    default: file.InventoryCategories,
  }))
)

const InventoryBrands = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/InventoryBrands').then((file) => ({
    default: file.InventoryBrands,
  }))
)

const InventorySuppliers = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/InventorySuppliers').then((file) => ({
    default: file.InventorySuppliers,
  }))
)

const Integrations = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/Integrations').then((file) => {
    return { default: file.Integrations }
  })
)

const ExternalVouchers = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/ExternalVouchers').then((file) => ({
    default: file.Vouchers,
  }))
)

const Developer = ReactLazyPreload(() =>
  import('../screens/MyAccount/Developer').then((file) => ({
    default: file.Developer,
  }))
)

const Details = ReactLazyPreload(() =>
  import('../screens/MyAccount/Details').then((file) => ({
    default: file.Details,
  }))
)

const NotificationSettings = ReactLazyPreload(() =>
  import('../screens/MyAccount/NotificationSettings').then((file) => ({
    default: file.NotificationSettings,
  }))
)

const Security = ReactLazyPreload(() =>
  import('../screens/MyAccount/Security').then((file) => ({
    default: file.Security,
  }))
)

const Roles = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/System/Roles').then((file) => {
    return { default: file.Roles }
  })
)
const Users = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/Site/Users').then((file) => {
    return { default: file.Users }
  })
)

const Billing = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/System/Billing').then((file) => {
    return { default: file.Billing }
  })
)

const OrganisationSecurity = ReactLazyPreload(() =>
  import('../components/SettingsDirectory/System/OrganisationSecurity').then((file) => ({
    default: file.OrganisationSecurity,
  }))
)

const AuditLogs = ReactLazyPreload(() =>
  import('screens/AuditLog').then((file) => ({ default: file.AuditLog }))
)

export const SettingsDirectoryRoute = (): Route[] => {
  return [
    {
      path: '/settings',
      name: 'Settings',
      component: SettingsDirectory,
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
  ]
}

export const useRoutes = (): Route[] => {
  const {
    data: {
      data: { has_table_reservation_integration: tableReservationsEnabled = false } = {},
    } = {},
  } = useShopSite(null, {
    refetchOnWindowFocus: false,
  })

  return [
    {
      path: '/settings/site/opening-hours',
      name: 'Opening hours',
      component: OpeningHours,
      category: 'site',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/site/zones',
      name: 'Zones',
      component: Zones,
      category: 'site',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/site/payment-types',
      name: 'Payment types',
      component: PaymentTypes,
      category: 'site',
      exact: true,
      permissions: [SHARED_SETTINGS_VIEW],
    },
    {
      path: '/settings/organisation/revenue-centres',
      name: 'Revenue centres',
      component: RevenueCentres,
      category: 'site',
      exact: true,
      permissions: [SHARED_SETTINGS_VIEW],
    },
    {
      path: '/offering-availability',
      name: 'Unavailable dates',
      component: OfferingAvailabilityCalendar,
      category: 'site',
      exact: true,
      permissions: [SETTINGS_MANAGE],
    },
    {
      path: '/settings/site/categories',
      name: 'Categories',
      component: Categories,
      category: 'site',
      exact: true,
      permissions: [SHARED_SETTINGS_VIEW],
    },
    {
      path: '/settings/site/voucher-delivery',
      name: 'Voucher Delivery',
      component: VoucherDelivery,
      category: 'site',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/site/appointment-enquiries',
      name: 'Appointment enquiries',
      component: AppointmentEnquiries,
      category: 'orders',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/resources/practitioners',
      name: 'Practitioners',
      component: Practitioners,
      category: 'resources',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/resources/rooms',
      name: 'Rooms',
      component: Rooms,
      category: 'resources',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/resources/bookable-areas',
      name: 'Bookable areas',
      component: BookableAreas,
      category: 'resources',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/resources/equipment',
      name: 'Equipment',
      component: Equipment,
      category: 'resources',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/resources/blocked-time-presets',
      name: 'Blocked times',
      component: BlockedTimePresets,
      category: 'resources',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/offerings/appointment-types',
      name: 'Appointment types',
      component: AppointmentTypes,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/offerings/session-types',
      name: 'Session types',
      component: SessionTypes,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/offerings/area-booking-types',
      name: 'Area booking types',
      component: AreaBookingTypes,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/offerings/retail-products',
      name: 'Retail products',
      component: RetailProducts,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/table-reservation-types',
      name: 'Table reservation types',
      component: TableReservationTypes,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
      hide: !tableReservationsEnabled,
    },
    {
      path: '/settings/offerings/package-types',
      name: 'Packages',
      component: Packages,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/offerings/courses',
      name: 'Courses',
      component: Courses,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/offerings/vouchers',
      name: 'Voucher types',
      component: VoucherTypes,
      category: 'offerings',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/clients/membership',
      name: 'Membership types',
      component: Membership,
      category: 'clients',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/clients/client-labels',
      name: 'Client labels',
      component: CustomerLabels,
      category: 'clients',
      exact: true,
      permissions: [SHARED_SETTINGS_VIEW],
    },
    {
      path: '/settings/clients/intake-forms',
      name: 'Intake form sections',
      component: IntakeFormBuilder,
      category: 'clients',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/shop',
      name: 'Shop settings',
      component: ShopSettings,
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/legal',
      name: 'Legal',
      component: Legal,
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/marketing-preferences',
      name: 'Marketing preferences',
      component: MarketingPreferences,
      category: 'marketing',
      exact: true,
      permissions: [SHARED_SETTINGS_VIEW],
    },
    {
      path: '/settings/email-branding',
      name: 'Email branding',
      component: EmailBranding,
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/orders/emails',
      name: 'Order emails',
      component: Emails,
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/sms-notifications',
      name: 'SMS notifications',
      component: SmsNotifications,
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/visit-types',
      name: 'Visit types',
      component: VisitTypes,
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/comms-locales',
      name: 'Communication locales',
      category: 'marketing',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/orders/discount-types-and-promo-codes',
      name: 'Discount types and promo codes',
      component: DiscountTypesAndPromoCodes,
      category: 'orders',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/orders/sales-channels',
      name: 'Sales channels',
      component: SalesChannels,
      category: 'orders',
      exact: true,
      permissions: [SHARED_SETTINGS_VIEW],
    },
    {
      path: '/settings/orders/order-labels',
      name: 'Order labels',
      component: OrderLabels,
      category: 'orders',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/orders/auto-checkout',
      name: 'Auto checkout',
      component: AutoCheckout,
      category: 'orders',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/orders/service-charge',
      name: 'Service charge and tips',
      component: ServiceCharge,
      category: 'orders',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/inventory/categories',
      name: 'Stock categories',
      component: InventoryCategories,
      category: 'inventory',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/inventory/brands',
      name: 'Stock brands',
      component: InventoryBrands,
      category: 'inventory',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/inventory/suppliers',
      name: 'Suppliers',
      component: InventorySuppliers,
      category: 'inventory',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/integrations',
      name: 'Manage integrations',
      component: Integrations,
      category: 'integrations',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/external-vouchers',
      name: 'Vouchers',
      component: ExternalVouchers,
      category: 'integrations',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/my-account/developer',
      name: 'Developer',
      component: Developer,
      category: 'integrations',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/my-account/details',
      name: 'Details',
      component: Details,
      category: 'account',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/my-account/notifications',
      name: 'Notifications',
      component: NotificationSettings,
      category: 'account',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/my-account/security',
      name: 'Security',
      component: Security,
      category: 'account',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/organisation-security',
      name: 'Organisation security',
      component: OrganisationSecurity,
      category: 'system',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/users',
      name: 'Manage users',
      component: Users,
      category: 'system',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/settings/roles',
      name: 'Manage roles',
      component: Roles,
      category: 'system',
      exact: true,
      permissions: [USERS_MANAGE],
    },
    {
      path: '/settings/billing',
      name: 'Billing',
      component: Billing,
      category: 'system',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
    {
      path: '/audit-logs',
      name: 'Audit logs',
      component: AuditLogs,
      category: 'system',
      exact: true,
      permissions: [SETTINGS_VIEW],
    },
  ]
}
